/**
 * Invoice preview screen.
 *
 * Loads an order or collection, shows it as a printable document, and offers:
 *   - Format toggle: A4 ↔ Thermal 80mm
 *   - Print  → window.print() with format-specific @page rules
 *   - Share  → Web Share API (falls back to copy link)
 *   - Back
 *
 * Route examples:
 *   /orders/:id/print
 *   /collections/:id/print
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Printer, Share2, ArrowRight, FileText, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import LoadingState from '@/components/shared/LoadingState';
import ErrorState from '@/components/shared/ErrorState';
import InvoicePrint from '@/components/InvoicePrint';
import { api } from '@/api/apiClient';
import { Visit, Collection } from '@/api/localStore';
import { shareElementAsImage } from '@/lib/shareDocument';
import { useCompanyInfo } from '@/lib/useCompanyInfo';

export default function InvoicePreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Decide kind from the URL path.
  const kind = location.pathname.includes('/collections/') ? 'collection' : 'order';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [format, setFormat] = useState('a4'); // 'a4' | 'thermal'
  const printRef = useRef(null);
  const company = useCompanyInfo();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Data passed directly via navigation state (e.g. right after
      //    creating a collection) — no API round-trip needed.
      const passed = location.state?.collectionData || location.state?.orderData;
      if (passed) {
        setData(passed);
        setLoading(false);
        return;
      }

      // 2. Otherwise load by id from the backend.
      if (kind === 'order') {
        const res = await api.get(`/sales/orders/${parseInt(id, 10)}`);
        if (res?.success && res.data) setData(res.data);
        else setError(res?.error || 'فشل تحميل الفاتورة');
      } else {
        const rows = await Collection.filter({ id });
        if (rows && rows[0]) setData(rows[0]);
        else {
          const one = await Collection.list().then((all) => all.find((c) => String(c.id) === String(id)));
          if (one) setData(one);
          else setError('لم يتم العثور على السند');
        }
      }
    } catch (e) {
      setError(e?.message || 'حدث خطأ');
    } finally {
      setLoading(false);
    }
  }, [id, kind, location.state]);

  useEffect(() => { load(); }, [load]);

  // Set the print format on <html> so the @page CSS can switch paper size.
  useEffect(() => {
    document.documentElement.setAttribute('data-print-format', format);
    return () => document.documentElement.removeAttribute('data-print-format');
  }, [format]);

  // Swap the actual printed PAPER size to match the selected format.
  // A `@page` at-rule can't be scoped by a selector/attribute, so the
  // static `html[data-print-format="thermal"] @page {…}` in index.css is
  // ignored by browsers and the sheet stayed A4 even in thermal mode.
  // We inject a real top-level @page rule here; appended last to <head>,
  // it wins the cascade over the default A4 rule.
  useEffect(() => {
    const STYLE_ID = 'sellgo-print-page-size';
    let style = document.getElementById(STYLE_ID);
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      document.head.appendChild(style);
    }
    style.textContent =
      format === 'thermal'
        ? '@media print { @page { size: 80mm auto; margin: 0; } }'
        : '@media print { @page { size: A4; margin: 10mm; } }';
    return () => {
      const el = document.getElementById(STYLE_ID);
      if (el) el.remove();
    };
  }, [format]);

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    if (!printRef.current) return;
    const base = kind === 'order' ? `فاتورة-${data?.name || ''}` : `سند-${data?.name || ''}`;
    const title = kind === 'order' ? `فاتورة ${data?.name || ''}` : `سند قبض ${data?.name || ''}`;
    const t = toast.loading('جاري تجهيز الصورة...');
    try {
      const res = await shareElementAsImage(printRef.current, { filename: `${base}.jpg`, title });
      toast.dismiss(t);
      if (res === 'downloaded') toast.success('تم حفظ الصورة');
    } catch {
      toast.dismiss(t);
      toast.error('تعذّر تجهيز الصورة');
    }
  };

  if (loading) return <div className="p-4"><LoadingState message="جاري تحميل الفاتورة..." /></div>;
  if (error) return <div className="p-4"><ErrorState message={error} onRetry={load} /></div>;
  if (!data) return null;

  return (
    <div className="flex flex-col min-h-full bg-gray-100" dir="rtl">
      {/* ── Toolbar (hidden when printing) ── */}
      <div className="no-print sticky top-0 z-20 bg-card border-b border-border px-4 py-3 flex items-center gap-3 shadow-sm">
        <button onClick={() => navigate(-1)} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-muted">
          <ArrowRight className="w-4 h-4 text-muted-foreground" />
        </button>
        <h1 className="flex-1 text-lg font-bold text-foreground truncate">
          {kind === 'order' ? 'معاينة الفاتورة' : 'معاينة السند'}
        </h1>

        {/* Format toggle */}
        <div className="flex bg-muted rounded-xl p-1">
          <button
            onClick={() => setFormat('a4')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              format === 'a4' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground'
            }`}
          >
            <FileText className="w-3.5 h-3.5" /> A4
          </button>
          <button
            onClick={() => setFormat('thermal')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              format === 'thermal' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground'
            }`}
          >
            <Receipt className="w-3.5 h-3.5" /> حراري 80مم
          </button>
        </div>
      </div>

      {/* ── Document preview ── */}
      <div className="flex-1 overflow-y-auto p-4 pb-32">
        <div ref={printRef} className="print-area mx-auto shadow-lg" style={{ background: 'white', borderRadius: 8 }}>
          <InvoicePrint kind={kind} data={data} format={format} company={company} />
        </div>
      </div>

      {/* ── Action bar (hidden when printing) ── */}
      <div className="no-print fixed bottom-0 right-0 left-0 bg-card border-t border-border px-4 py-3 z-30 flex gap-2">
        <Button onClick={handleShare} variant="outline" className="flex-1 rounded-xl h-12 gap-2">
          <Share2 className="w-4 h-4" /> مشاركة
        </Button>
        <Button onClick={handlePrint} className="flex-1 bg-primary rounded-xl h-12 gap-2">
          <Printer className="w-4 h-4" /> طباعة / حفظ PDF
        </Button>
      </div>
    </div>
  );
}
